import { useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  itemsApi, purchaseInvoicesApi, vendorsApi, warehousesApi,
  type ItemDto, type PurchaseRateDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useFeatureFlags } from "@/store/featureFlags";
import { todayIso } from "@/shared/datetime";
import {
  resolvePurchaseRate, purchaseRateLabel, purchaseRateHint, isPurchaseRateUnreliable,
} from "@/shared/purchaseRates";

interface DraftLine {
  key: string;
  item: ItemDto | null;
  batchNo: string;
  expiryDate: string;
  qty: number;
  rate: number;
  /** MRP printed on the pack. 0 = not stated, batch stays unpriced. */
  mrp: number;
  taxAmount: number;
  discountAmount: number;
  /** Where `rate` was defaulted from, for the caption under the field. Null once typed over. */
  rateSource: PurchaseRateDto | null;
  /** Set when the biller types a rate, so re-resolving never overwrites their figure. */
  rateTouched: boolean;
}
const blank = (): DraftLine => ({
  key: crypto.randomUUID(),
  item: null, batchNo: "", expiryDate: "",
  qty: 0, rate: 0, mrp: 0, taxAmount: 0, discountAmount: 0,
  rateSource: null, rateTouched: false,
});

const taxOn = (qty: number, rate: number, gstRate?: number) =>
  Math.round(qty * rate * (gstRate ?? 0)) / 100;

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

/**
 * Direct Purchase Invoice — bill-entry desk only. Save = approve + receive stock + post JE
 * in one shot. The DPI- numbering series distinguishes these from the normal PI- flow.
 * Posted is terminal — corrections via reversal/debit note only.
 */
export function CreateDirectPurchaseInvoicePage() {
  const navigate = useNavigate();
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const [vendorId, setVendorId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [documentDate, setDocumentDate] = useState(todayIso);
  const [vendorInvoiceNo, setVendorInvoiceNo] = useState("");
  const [vendorInvoiceDate, setVendorInvoiceDate] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blank()]);
  const [resolvingRate, setResolvingRate] = useState<Record<string, boolean>>({});
  /** Per-line request counter, so an out-of-order rate lookup cannot overwrite a newer one. */
  const rateReqSeq = useRef<Record<string, number>>({});

  const vendors = useQuery({
    queryKey: ["vendors", "active"],
    queryFn: () => vendorsApi.list({ pageSize: 200, isActive: true }),
  });
  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });
  const items = useQuery({
    queryKey: ["items", "picker", search],
    queryFn: () => itemsApi.list({ search: search || undefined, pageSize: 50 }),
  });

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map(l => {
      if (l.key !== key) return l;
      const next = { ...l, ...patch };
      // A typed rate is the biller's own figure — usually straight off the vendor's bill, which
      // is the most authoritative source there is. Nothing re-resolves over it.
      if ("rate" in patch && !("rateSource" in patch)) {
        next.rateTouched = true;
        next.rateSource = null;
      }
      // A new item invalidates the old one's rate outright; fillRate() supplies the real one.
      if ("item" in patch) {
        next.rate = 0;
        next.rateSource = null;
        next.rateTouched = false;
      }
      // Auto-calculate GST from the item's tax rate whenever item / qty / rate change.
      if ("item" in patch || "qty" in patch || "rate" in patch)
        next.taxAmount = taxOn(next.qty, next.rate, next.item?.gstRate);
      return next;
    }));

  /**
   * Default a line's rate from the vendor-item rate, then purchase history, then MRP — the same
   * precedence the reorder path has always used. Never overwrites a rate the biller typed.
   *
   * This screen posts on save, so a wrong rate here lands in Batch.CostPrice with no draft stage
   * to catch it — which makes the defaulted figure worth getting right more than anywhere else.
   */
  const fillRate = async (key: string, item: ItemDto | null, forVendorId: string) => {
    if (!item) return;
    // Picking an item and then switching vendor puts two lookups in flight for one line; without
    // a sequence the slower (older-vendor) answer can land last and win.
    const seq = (rateReqSeq.current[key] ?? 0) + 1;
    rateReqSeq.current[key] = seq;

    setResolvingRate(s => ({ ...s, [key]: true }));
    const resolved = await resolvePurchaseRate(item.id, forVendorId || undefined, item.defaultMrp);
    if (rateReqSeq.current[key] !== seq) return;   // superseded — leave the newer one to finish

    setResolvingRate(s => { const n = { ...s }; delete n[key]; return n; });
    setLines(ls => ls.map(l => {
      // Stale answer: the line changed item, or was priced by hand, while the lookup was out.
      if (l.key !== key || l.item?.id !== item.id || l.rateTouched) return l;
      return {
        ...l,
        rate: resolved.rate,
        rateSource: resolved,
        taxAmount: taxOn(l.qty, resolved.rate, l.item?.gstRate),
      };
    }));
  };

  /** A different vendor is a different price. Re-ask for every rate this screen defaulted. */
  const changeVendor = (id: string) => {
    setVendorId(id);
    for (const l of lines) if (l.item && !l.rateTouched) void fillRate(l.key, l.item, id);
  };

  const remove = (key: string) =>
    setLines(ls => ls.length === 1 ? ls : ls.filter(l => l.key !== key));

  const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0);
  const taxTotal = lines.reduce((s, l) => s + l.taxAmount, 0);
  const totalAmount = subTotal + taxTotal - discountAmount;

  // Lines still carrying a retail MRP nobody confirmed. A warning rather than a validation
  // failure — the biller may be paying list price — but this screen posts on save, so it is the
  // last point at which the figure can be questioned.
  const mrpDefaulted = lines
    .map((l, i) => ({ line: l, no: i + 1 }))
    .filter(({ line }) => line.item && !line.rateTouched
      && line.rateSource && isPurchaseRateUnreliable(line.rateSource.source));

  const valid = vendorId && warehouseId &&
    lines.every(l => l.item && l.batchNo.trim() && l.qty > 0 && l.rate >= 0
      // Mirrors the server rule: billing is capped at MRP, so an MRP under the purchase rate
      // means the batch could only ever be sold at a loss.
      && (l.mrp === 0 || l.mrp >= l.rate));

  const mutation = useMutation({
    meta: { successMessage: "Direct purchase invoice created" },
    mutationFn: () => purchaseInvoicesApi.createDirect({
      vendorId,
      warehouseId,
      documentDate: new Date(documentDate).toISOString(),
      vendorInvoiceNo: vendorInvoiceNo || undefined,
      vendorInvoiceDate: vendorInvoiceDate
        ? new Date(vendorInvoiceDate).toISOString()
        : undefined,
      discountAmount,
      notes: notes || undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id,
        batchNo: l.batchNo.trim(),
        expiryDate: l.expiryDate || undefined,
        qty: l.qty, rate: l.rate,
        // Blank means the invoice did not state one — send nothing rather than a zero.
        mrp: l.mrp > 0 ? l.mrp : undefined,
        taxAmount: l.taxAmount, discountAmount: l.discountAmount,
      })),
    }),
    onSuccess: (r) => navigate(`/procurement/purchase-invoices/${r.id}`),
  });

  // Direct PI is a feature-flagged (MVP2) capability — the API returns 403 when off.
  // Guard the page too, in case the route is reached directly (bookmark / deep link).
  if (!flagsLoading && !isEnabled("procurement.direct-pi")) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small"
            onClick={() => navigate("/procurement/purchase-invoices")}>Back to invoices</Button>}>
          Direct Purchase Invoice is not enabled for this tenant. An administrator can turn it on
          under <b>Admin → Feature Flags</b> (<code>procurement.direct-pi</code>).
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>New Direct Purchase Invoice</Typography>
        <Chip size="small" color="warning" variant="outlined" label="Direct" />
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <b>Save will post immediately.</b> The system creates the PI with a <b>DPI-</b> number,
        receives stock into the chosen warehouse, and posts the GL journal (DR Inventory / DR GST / CR AP)
        in one operation. Posted is terminal — corrections require a reversal/debit note.
      </Alert>

      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Vendor *" sx={{ minWidth: 240 }}
            value={vendorId} onChange={(e) => changeVendor(e.target.value)}>
            <MenuItem value="">— pick vendor —</MenuItem>
            {vendors.data?.items.map(v => (
              <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Receiving warehouse *" sx={{ minWidth: 240 }}
            value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <MenuItem value="">— pick warehouse —</MenuItem>
            {warehouses.data?.items.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="PI date *" size="small"
            InputLabelProps={{ shrink: true }}
            value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField label="Vendor invoice #" size="small" sx={{ minWidth: 200 }}
            value={vendorInvoiceNo} onChange={(e) => setVendorInvoiceNo(e.target.value)} />
          <TextField type="date" label="Vendor invoice date" size="small"
            InputLabelProps={{ shrink: true }}
            value={vendorInvoiceDate} onChange={(e) => setVendorInvoiceDate(e.target.value)} />
          <TextField type="number" label="Header discount" size="small" sx={{ width: 160 }}
            value={discountAmount} onChange={(e) => setDiscountAmount(Number(e.target.value) || 0)}
            inputProps={{ min: 0, step: "0.01" }} />
        </Stack>

        <TextField label="Notes" fullWidth multiline minRows={2} size="small"
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }} />

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines (stock items only)</Typography>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(ls => [...ls, blank()])}>Add line</Button>
        </Stack>

        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ minWidth: 260 }}>Item *</TableCell>
            <TableCell sx={{ width: 140 }}>Batch # *</TableCell>
            <TableCell sx={{ width: 150 }}>Expiry</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>Qty *</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Rate *</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>MRP</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>Tax</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>Disc</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>Line total</TableCell>
            <TableCell sx={{ width: 60 }} />
          </TableRow></TableHead>
          <TableBody>
            {lines.map((l, idx) => (
              <TableRow key={l.key}>
                <TableCell>
                  <Autocomplete size="small"
                    options={items.data?.items ?? []}
                    getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                    value={l.item}
                    onChange={(_, v) => { update(l.key, { item: v }); void fillRate(l.key, v, vendorId); }}
                    onInputChange={(_, v) => setSearch(v)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(p) => <TextField {...p} placeholder={`Line ${idx + 1}`} />}
                    loading={items.isLoading}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={l.batchNo}
                    onChange={(e) => update(l.key, { batchNo: e.target.value })} />
                </TableCell>
                <TableCell>
                  <TextField type="date" size="small" fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={l.expiryDate}
                    onChange={(e) => update(l.key, { expiryDate: e.target.value })} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 90 }}
                    value={l.qty}
                    onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 100 }}
                    value={l.rate}
                    onChange={(e) => update(l.key, { rate: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                  {resolvingRate[l.key] ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      resolving…
                    </Typography>
                  ) : l.rateSource && (
                    <Tooltip title={purchaseRateHint(l.rateSource)}>
                      <Typography variant="caption" display="block" sx={{ cursor: "help" }}
                        color={isPurchaseRateUnreliable(l.rateSource.source)
                          ? "warning.main" : "text.secondary"}
                        data-testid={`dpi-rate-source-${idx}`}>
                        {purchaseRateLabel(l.rateSource)}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 100 }}
                    value={l.mrp || ""}
                    placeholder="—"
                    onChange={(e) => update(l.key, { mrp: Number(e.target.value) || 0 })}
                    error={l.mrp > 0 && l.mrp < l.rate}
                    helperText={l.mrp > 0 && l.mrp < l.rate ? "below rate" : undefined}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 90 }}
                    value={l.taxAmount}
                    onChange={(e) => update(l.key, { taxAmount: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 90 }}
                    value={l.discountAmount}
                    onChange={(e) => update(l.key, { discountAmount: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  {inr.format(l.qty * l.rate + l.taxAmount - l.discountAmount)}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {mrpDefaulted.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="dpi-mrp-default-warning">
            No purchase rate is on record for {mrpDefaulted.length === 1 ? "line" : "lines"}{" "}
            {mrpDefaulted.map(({ no }) => no).join(", ")}, so {mrpDefaulted.length === 1 ? "it is" : "they are"}{" "}
            showing the item&apos;s retail MRP — the selling price, not the vendor&apos;s. This
            invoice posts on save and writes the rate straight to the batch cost, so check it
            against the vendor&apos;s bill before saving.
          </Alert>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={4} sx={{ mt: 3 }}>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Subtotal</Typography>
            <Typography>{inr.format(subTotal)}</Typography>
          </Stack>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Tax</Typography>
            <Typography>{inr.format(taxTotal)}</Typography>
          </Stack>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Discount</Typography>
            <Typography>-{inr.format(discountAmount)}</Typography>
          </Stack>
          <Stack alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6">{inr.format(totalAmount)}</Typography>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/procurement/purchase-invoices")}>Cancel</Button>
          <Button variant="contained" color="warning"
            disabled={!valid || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>
            Save &amp; Post
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
